/**
 * Parseo de archivos TXT PUI (insumo generado o respuesta DQ de la plataforma SEP).
 */

export const PUI_TXT_COLUMN_COUNT = 47

export const PUI_TXT_FIELD_KEYS = [
	'flujo',
	'anioEscolar',
	'cicloEscolar',
	'nombre',
	'primerApellido',
	'segundoApellido',
	'genero',
	'curp',
	'segmentoRaiz',
	'fechaNacimiento',
	'correoInstitucional',
	'correoElectronico',
	'telefono',
	'nacionalidad',
	'paisNacimiento',
	'entidadNacimiento',
	'paisResidencia',
	'entidadResidencia',
	'paisProcedencia',
	'entidadProcedencia',
	'idioma',
	'segundoIdioma',
	'necesidadesEspeciales',
	'tipoDiscapacidad',
	'aptitudSobresaliente',
	'claveInstitucion',
	'CCT',
	'claveCarrera',
	'creditosTotales',
	'creditosObtenidos',
	'nivelEducativo',
	'modalidadEducativa',
	'opcionEducativa',
	'numeroAcuerdoRvoe',
	'fechaAcuerdoRvoe',
	'turno',
	'cicloEscolarInstitucional',
	'gradoAvance',
	'periodo',
	'duracionPeriodo',
	'fechaInicioPeriodo',
	'antecedenteAcademico',
	'tipoAntecedenteAcademico',
	'matriculaInstitucional',
	'fechaInicioEstudios',
	'origenEstudios',
	'tipoBajaAcademica',
	'motivoBaja',
	'numeroLinea',
	'mensajeError2',
	'mensajeError'
] as const

export type TPuiTxtFieldKey = (typeof PUI_TXT_FIELD_KEYS)[number]

export const PUI_TXT_FIELD_LABELS: Record<TPuiTxtFieldKey, string> = {
	flujo: 'Flujo',
	anioEscolar: 'Año escolar',
	cicloEscolar: 'Ciclo escolar',
	nombre: 'Nombre',
	primerApellido: 'Primer apellido',
	segundoApellido: 'Segundo apellido',
	genero: 'Sexo',
	curp: 'CURP',
	segmentoRaiz: 'Segmento raíz',
	fechaNacimiento: 'Fecha de nacimiento',
	correoInstitucional: 'Correo institucional',
	correoElectronico: 'Correo personal',
	telefono: 'Teléfono',
	nacionalidad: 'Nacionalidad',
	paisNacimiento: 'País de nacimiento',
	entidadNacimiento: 'Entidad de nacimiento',
	paisResidencia: 'País de residencia',
	entidadResidencia: 'Entidad de residencia',
	paisProcedencia: 'País de procedencia',
	entidadProcedencia: 'Entidad de procedencia',
	idioma: 'Lengua materna',
	segundoIdioma: 'Segunda lengua',
	necesidadesEspeciales: 'Necesidad educativa',
	tipoDiscapacidad: 'Tipo de discapacidad',
	aptitudSobresaliente: 'Aptitud sobresaliente',
	claveInstitucion: 'Clave institución',
	CCT: 'CCT',
	claveCarrera: 'Clave carrera',
	creditosTotales: 'Créditos totales',
	creditosObtenidos: 'Créditos acumulados',
	nivelEducativo: 'Nivel educativo',
	modalidadEducativa: 'Modalidad',
	opcionEducativa: 'Opción educativa',
	numeroAcuerdoRvoe: 'Número RVOE',
	fechaAcuerdoRvoe: 'Fecha RVOE',
	turno: 'Turno',
	cicloEscolarInstitucional: 'Ciclo institucional',
	gradoAvance: 'Grado',
	periodo: 'Periodo',
	duracionPeriodo: 'Duración del periodo',
	fechaInicioPeriodo: 'Fecha inicio periodo',
	antecedenteAcademico: 'Antecedente académico',
	tipoAntecedenteAcademico: 'Tipo antecedente académico',
	matriculaInstitucional: 'Matrícula',
	fechaInicioEstudios: 'Fecha inicio estudios',
	origenEstudios: 'Origen estudios',
	tipoBajaAcademica: 'Tipo de baja',
	motivoBaja: 'Motivo de baja',
	numeroLinea: 'Número de línea',
	mensajeError2: 'Mensaje de error (2)',
	mensajeError: 'Código de error'
}

export interface IPuiTxtRecord {
	rowIndex: number
	nombreCompleto: string
	curp: string
	matricula: string
	mensajeError: string
	codigosError: string[]
	tieneError: boolean
}

export interface IPuiTxtParseResult {
	hasHeader: boolean
	isErrorFile: boolean
	records: IPuiTxtRecord[]
	parseErrors: string[]
}

function normalizeHeaderToken(header: string): string {
	return header.trim().replace(/\s+/g, '').replace(/_/g, '').toUpperCase()
}

function isHeaderLine(cells: string[]): boolean {
	const first = normalizeHeaderToken(cells[0] ?? '')
	return (
		first === 'FLUJO' ||
		cells.some(cell => {
			const token = normalizeHeaderToken(cell)
			return token === 'NOMBREALUMNO' || token === 'CURPALUMNO' || token === 'MENSAJEERROR'
		})
	)
}

function buildRecordFields(values: string[]): Partial<Record<TPuiTxtFieldKey, string>> {
	const fields: Partial<Record<TPuiTxtFieldKey, string>> = {}
	const keys = [...PUI_TXT_FIELD_KEYS]

	for (let i = 0; i < keys.length; i++) {
		const raw = values[i]
		if (raw === undefined) continue
		const trimmed = raw.trim()
		if (trimmed !== '') {
			fields[keys[i]] = trimmed
		}
	}
	return fields
}

function buildNombreCompleto(fields: Partial<Record<TPuiTxtFieldKey, string>>): string {
	const parts = [fields.nombre, fields.primerApellido, fields.segundoApellido].filter(Boolean)
	return parts.join(' ').trim() || '—'
}

/** Separa códigos DQ concatenados con coma (p. ej. "A_WF_CURP ,A_IC_NOMBRE"). */
export function parsePuiErrorCodes(mensajeError: string): string[] {
	if (!mensajeError.trim()) return []
	return mensajeError
		.split(',')
		.map(codigo => codigo.trim())
		.filter(Boolean)
}

function buildRecord(rowIndex: number, values: string[]): IPuiTxtRecord {
	const fields = buildRecordFields(values)
	const mensajeError = fields.mensajeError ?? fields.mensajeError2 ?? ''
	const codigosError = parsePuiErrorCodes(mensajeError)
	return {
		rowIndex,
		nombreCompleto: buildNombreCompleto(fields),
		curp: fields.curp ?? '—',
		matricula: fields.matriculaInstitucional ?? '—',
		mensajeError,
		codigosError,
		tieneError: codigosError.length > 0
	}
}

export function parsePuiTxtContent(content: string): IPuiTxtParseResult {
	const parseErrors: string[] = []
	const normalizedContent = content.replace(/^\uFEFF/, '').trim()

	if (!normalizedContent) {
		return { hasHeader: false, isErrorFile: false, records: [], parseErrors: ['El archivo está vacío.'] }
	}

	const lines = normalizedContent
		.split(/\r?\n/)
		.map(line => line.trim())
		.filter(line => line.length > 0)

	if (lines.length === 0) {
		return { hasHeader: false, isErrorFile: false, records: [], parseErrors: ['El archivo no contiene líneas válidas.'] }
	}

	const firstCells = lines[0].split('|') // Encabezado del archivo
	const hasHeader = isHeaderLine(firstCells) // Verifica si el archivo tiene encabezado

	const dataLines = hasHeader ? lines.slice(1) : lines // Si tiene encabezado, se omiten las primeras líneas
	const records: IPuiTxtRecord[] = [] // Se crean los registros

	dataLines.forEach((line, index) => {
		const values = line.split('|')
		const expectedCount = PUI_TXT_COLUMN_COUNT // Se espera que el archivo tenga 47 columnas

		if (values.length < expectedCount - 2) {
			parseErrors.push(`Línea ${hasHeader ? index + 2 : index + 1}: número de columnas insuficiente (${values.length}).`)
		}

		records.push(buildRecord(hasHeader ? index + 2 : index + 1, values))
	})
	const isErrorFile = records.some(record => record.tieneError)

	return {
		hasHeader,
		isErrorFile,
		records,
		parseErrors
	}
}
